import { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, User, AlertTriangle } from 'lucide-react';

// Type definitions
type SignupData = {
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  password: string;
};

type LoginData = {
  email: string;
  password: string;
};

// Helper function to set auth token
const setAuthToken = (token: string) => {
  localStorage.setItem('csense_auth_token', token);
  console.log('🔐 Token stored:', token ? `${token.substring(0, 20)}...` : 'null');
};

interface AuthProps {
  onAuthSuccess: (userData: any) => void;
  onBack?: () => void;
}

// Local API request function
const makeApiRequest = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://csense.airivu.in';
  const token = localStorage.getItem('csense_auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export function Auth({ onAuthSuccess, onBack }: AuthProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [errors, setErrors] = useState<{
    general?: string;
  }>({});
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  // Focus ring animation state
  const [ringFocused, setRingFocused] = useState(false);

  const handleLogin = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      const credentials: LoginData = {
        email: loginData.email,
        password: loginData.password
      };

      const response = await makeApiRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials)
      });

      setAuthToken(response.access_token);
      onAuthSuccess({
        type: 'login',
        user: {
          email: response.user.email,
          name: `${response.user.first_name} ${response.user.last_name}`,
          firstName: response.user.first_name,
          lastName: response.user.last_name,
          phone: response.user.phone,
          isExistingUser: true,
          provider: response.user.provider,
          onboardingCompleted: response.user.onboarding_completed,
          cameraCount: response.user.camera_count || 0
        },
        token: response.access_token
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed. Please try again.';
      const friendlyMessage = errorMessage.toLowerCase().includes('failed to fetch')
        ? 'Cannot reach authentication server. Check API URL, SSL certificate, CORS, or backend status.'
        : errorMessage;
      setErrors({ general: friendlyMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async () => {
    setIsLoading(true);
    setErrors({});
    
    if (signupData.password !== signupData.confirmPassword) {
      setErrors({ general: 'Passwords do not match' });
      setIsLoading(false);
      return;
    }

    try {
      const userData: SignupData = {
        email: signupData.email,
        first_name: signupData.firstName,
        last_name: signupData.lastName,
        phone: signupData.phone || undefined,
        password: signupData.password
      };

      const response = await makeApiRequest('/api/auth/signup', {
        method: 'POST',
        body: JSON.stringify(userData)
      });

      setAuthToken(response.access_token);
      onAuthSuccess({
        type: 'signup',
        user: {
          email: response.user.email,
          name: `${response.user.first_name} ${response.user.last_name}`,
          firstName: response.user.first_name,
          lastName: response.user.last_name,
          phone: response.user.phone,
          isExistingUser: false,
          provider: response.user.provider,
          onboardingCompleted: response.user.onboarding_completed || false,
          cameraCount: response.user.camera_count || 0
        },
        token: response.access_token
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Signup failed. Please try again.';
      const friendlyMessage = errorMessage.toLowerCase().includes('failed to fetch')
        ? 'Cannot reach authentication server. Check API URL, SSL certificate, CORS, or backend status.'
        : errorMessage;
      setErrors({ general: friendlyMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="enterprise-app">
      {/* Background Layer */}
      <div className="enterprise-bg"></div>
      <div className="enterprise-noise"></div>
      
      {/* Particles effect simulated */}
      <div className="enterprise-particles" style={{ display: 'none' }}></div>

      {/* INTELLIGENCE RING SYSTEM */}
      <div className={`ring-wrap ${ringFocused ? 'focused' : ''}`}>
        <div className="r-ticks"></div>
        <div className="r-outer"></div>
        <div className="r-scan"></div>
        <div className="r-mid"></div>
        <div className="r-inner"></div>
        <div className="r-core"></div>
        <div className="r-glow"></div>
        <div className="r-pulse"></div>
      </div>

      <div className="enterprise-shell shell">
        <div className="card active">
          {/* Video Panel */}
          <div className="card-video-wrap">
            <video autoPlay loop muted playsInline preload="auto" style={{ width: '100%', height: '100%', objectFit: 'cover' }}>
              <source src="/login-loop.mp4" type="video/mp4" />
            </video>
            <div className="card-video-overlay"></div>
            <div className="card-video-ring"></div>
            <div className="card-video-live cursor-pointer" onClick={() => setActiveTab(activeTab === 'login' ? 'signup' : 'login')}>
              {activeTab === 'login' ? 'LOGIN ACTIVE' : 'SIGN UP ACTIVE'}
            </div>
          </div>

          {/* Logo Brand */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '32px' }}>
            <h1 className="text-white font-black" style={{ fontSize: '36px', letterSpacing: '-0.04em', margin: 0, lineHeight: 1, textShadow: '0 4px 15px rgba(0,0,0,0.5)' }}>3RDI</h1>
            <span style={{ fontSize: '11px', color: '#60a5fa', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4em', marginTop: '8px', textShadow: '0 0 10px rgba(96, 165, 250, 0.4)' }}>Vision Intelligence</span>
          </div>

          {/* Error Message */}
          <div className={`error-bar ${errors.general ? 'show' : ''}`} style={{ display: errors.general ? 'flex' : 'none', alignItems: 'center' }}>
            <AlertTriangle size={18} style={{ marginRight: '8px', display: 'inline-block' }} /><span>{errors.general}</span>
          </div>

          {activeTab === 'login' ? (
            /* LOGIN FORM */
            <div className="tab-pane active" onFocus={() => setRingFocused(true)} onBlur={() => setRingFocused(false)}>
              <div className="field">
                <label style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>Email Address</label>
                <div className="input-wrap">
                  <Mail size={16} />
                  <input
                    className="input"
                    type="email"
                    placeholder="admin@csense.ai"
                    value={loginData.email}
                    onChange={e => setLoginData({...loginData, email: e.target.value})}
                  />
                  <div className="ripple-bar"></div>
                </div>
              </div>

              <div className="field">
                <label style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>Password</label>
                <div className="input-wrap">
                  <Lock size={16} />
                  <input
                    className="input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={loginData.password}
                    onChange={e => setLoginData({...loginData, password: e.target.value})}
                  />
                  <button type="button" className="eye" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                  <div className="ripple-bar"></div>
                </div>
              </div>

              <div className="forgot"><a href="#">Forgot password?</a></div>

              <button className={`btn-login ${isLoading ? 'loading' : ''}`} onClick={handleLogin}>
                <span className="btn-text">Enter System</span>
              </button>
            </div>
          ) : (
            /* SIGN UP FORM (Simplified grid for glassmorphism) */
            <div className="tab-pane active" onFocus={() => setRingFocused(true)} onBlur={() => setRingFocused(false)}>
              <div className="grid-2" style={{ gap: '10px' }}>
                <div className="field" style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>First Name</label>
                  <div className="input-wrap">
                    <User size={16} />
                    <input className="input" style={{ paddingLeft: '32px', fontSize: '12px' }} value={signupData.firstName} onChange={e => setSignupData({...signupData, firstName: e.target.value})} />
                  </div>
                </div>
                <div className="field" style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>Last Name</label>
                  <div className="input-wrap">
                    <User size={16} />
                    <input className="input" style={{ paddingLeft: '32px', fontSize: '12px' }} value={signupData.lastName} onChange={e => setSignupData({...signupData, lastName: e.target.value})} />
                  </div>
                </div>
              </div>

              <div className="field" style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>Email Address</label>
                <div className="input-wrap">
                  <Mail size={16} />
                  <input className="input" type="email" style={{ paddingLeft: '32px', fontSize: '12px' }} value={signupData.email} onChange={e => setSignupData({...signupData, email: e.target.value})} />
                </div>
              </div>

              <div className="grid-2" style={{ gap: '10px' }}>
                <div className="field" style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>Password</label>
                  <div className="input-wrap">
                    <Lock size={16} />
                    <input className="input" type="password" style={{ paddingLeft: '32px', fontSize: '12px' }} value={signupData.password} onChange={e => setSignupData({...signupData, password: e.target.value})} />
                  </div>
                </div>
                <div className="field" style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>Confirm</label>
                  <div className="input-wrap">
                    <Lock size={16} />
                    <input className="input" type="password" style={{ paddingLeft: '32px', fontSize: '12px' }} value={signupData.confirmPassword} onChange={e => setSignupData({...signupData, confirmPassword: e.target.value})} />
                  </div>
                </div>
              </div>

              <button className={`btn-login ${isLoading ? 'loading' : ''}`} onClick={handleSignup} style={{ marginTop: '10px' }}>
                <span className="btn-text">Create Account</span>
              </button>
            </div>
          )}

          <div className="divider" style={{ margin: '18px 0', fontSize: '10px' }}>
            Secured by <span style={{ color: 'var(--vg)', fontWeight: 600 }}>BeAItle</span>
          </div>
          
          <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--t2)', cursor: 'pointer' }} onClick={() => setActiveTab(activeTab === 'login' ? 'signup' : 'login')}>
            {activeTab === 'login' ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
          </div>

        </div>
      </div>

      <style>{`
        /* Embedded specific Login styles that don't belong in global enterprise-glass.css */
        .ring-wrap { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 560px; height: 560px; z-index: 3; pointer-events: none; transition: transform 0.8s cubic-bezier(.22, 1, .36, 1), opacity 0.6s ease; }
        .ring-wrap.focused { transform: translate(-50%, -50%) scale(1.04); }
        .r-outer { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(146, 77, 191, .0); animation: rx 30s linear infinite; }
        .r-outer::before { content: ''; position: absolute; inset: -1px; border-radius: 50%; border: 1px dashed rgba(146, 77, 191, .12); animation: rx 50s linear infinite reverse; }
        .r-outer::after { content: ''; position: absolute; inset: 10px; border-radius: 50%; border: 1px solid transparent; border-bottom-color: var(--cyan); border-left-color: rgba(34, 211, 238, .25); animation: rx 18s linear infinite reverse; }
        .r-mid { position: absolute; inset: 70px; border-radius: 50%; border: 1.5px solid transparent; border-top-color: var(--vg); border-right-color: rgba(169, 112, 255, .3); animation: rx 14s linear infinite; }
        .r-mid::after { content: ''; position: absolute; inset: 6px; border-radius: 50%; border: 1px solid rgba(169, 112, 255, .15); animation: rx 22s linear infinite reverse; }
        .r-inner { position: absolute; inset: 150px; border-radius: 50%; border: 1px solid rgba(34, 211, 238, .25); animation: rx 10s linear infinite reverse; }
        .r-core { position: absolute; inset: 210px; border-radius: 50%; background: radial-gradient(circle, rgba(34, 211, 238, .2) 0%, rgba(146, 77, 191, .12) 50%, transparent 80%); animation: breathe 4s ease-in-out infinite; }
        .r-core::before { content: ''; position: absolute; inset: 20px; border-radius: 50%; background: radial-gradient(circle, rgba(34, 211, 238, .35) 0%, transparent 70%); animation: breathe 4s ease-in-out infinite reverse; }
        .r-scan { position: absolute; inset: 70px; border-radius: 50%; background: conic-gradient(from 0deg, rgba(169, 112, 255, .12) 0deg, transparent 60deg, transparent 360deg); animation: rx 6s linear infinite; }
        
        .r-glow { position: absolute; inset: 60px; border-radius: 50%; border: 2px solid rgba(169, 112, 255, 0); box-shadow: 0 0 0 rgba(169, 112, 255, 0); transition: all 1.2s ease; }
        .ring-wrap.focused .r-glow { border-color: rgba(169, 112, 255, .2); box-shadow: 0 0 40px rgba(169, 112, 255, .15), inset 0 0 40px rgba(169, 112, 255, .08); }
        .r-pulse { position: absolute; inset: 210px; border-radius: 50%; border: 1px solid var(--cyan); opacity: 0; transform: scale(1); animation: none; }
        .ring-wrap.focused .r-pulse { animation: pulseOut 2s ease-out infinite; }

        .shell { justify-content: center; align-items: center; }
        .card { width: 420px; padding: 44px 40px 38px; background: rgba(255, 255, 255, 0.05); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 24px; box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 40px 80px rgba(0, 0, 0, 0.4); position: relative; transition: border-color .4s ease, box-shadow .4s ease; z-index: 10; overflow: hidden; }
        .card::before { content: ''; position: absolute; top: -1px; left: 20%; right: 20%; height: 1px; background: linear-gradient(90deg, transparent, rgba(169, 112, 255, .4), transparent); opacity: 0; transition: opacity .4s ease; }
        .card.active::before { opacity: 1; }
        .card.active { border-color: rgba(169, 112, 255, .15); box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 40px 80px rgba(0, 0, 0, .5), 0 0 60px rgba(146, 77, 191, .08); }

        .card-video-wrap { position: relative; width: 100%; aspect-ratio: 24/16; border-radius: 14px; overflow: hidden; margin-bottom: 28px; background: #000; }
        .card-video-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(11, 11, 15, .25) 0%, rgba(11, 11, 15, .55) 100%); pointer-events: none; }
        .card-video-ring { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
        .card-video-ring::before { content: ''; width: 64px; height: 64px; border-radius: 50%; border: 1.5px solid rgba(169, 112, 255, .5); box-shadow: 0 0 20px rgba(169, 112, 255, .3), inset 0 0 20px rgba(34, 211, 238, .1); animation: breathe 4s ease-in-out infinite; }
        .card-video-live { position: absolute; top: 10px; right: 12px; font-size: 10px; color: var(--cyan); background: rgba(0, 0, 0, .5); border: 1px solid rgba(34, 211, 238, .2); padding: 3px 8px; border-radius: 6px; backdrop-filter: blur(6px); display: flex; align-items: center; gap: 5px; font-weight: 600; cursor: pointer; user-select: none; }
        .card-video-live::before { content: ''; width: 5px; height: 5px; border-radius: 50%; background: var(--cyan); animation: blink 2s ease-in-out infinite; }
        .card-video-live:hover { background: rgba(34,211,238,.15); }

        .field { margin-bottom: 20px; position: relative; }
        .field label { display: block; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px; color: var(--t2); margin-bottom: 8px; transition: color .3s ease; }
        .field:focus-within label { color: var(--vg); }
        .input-wrap { position: relative; }
        .input-wrap i { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); font-size: 13px; font-style: normal; color: var(--t2); transition: color .3s ease; pointer-events: none; }
        .field:focus-within .input-wrap i { color: var(--vg); }
        .input { width: 100%; background: rgba(0, 0, 0, .3); border: 1px solid rgba(255, 255, 255, .06); border-radius: 10px; padding: 13px 14px 13px 40px; font-size: 14px; color: var(--t1); font-family: 'Space Grotesk', sans-serif; outline: none; transition: all .3s ease; }
        .input:focus { background: rgba(0, 0, 0, .4); border-color: rgba(169, 112, 255, .4); box-shadow: 0 0 0 3px rgba(169, 112, 255, .08), inset 0 0 20px rgba(169, 112, 255, .04); }
        .input::placeholder { color: var(--t2); opacity: .6; }

        .ripple-bar { position: absolute; bottom: 0; left: 50%; transform: translateX(-50%); width: 0; height: 2px; border-radius: 1px; background: linear-gradient(90deg, var(--violet), var(--cyan)); transition: width .4s cubic-bezier(.22, 1, .36, 1); border-bottom-left-radius: 0; border-bottom-right-radius: 0; }
        .field:focus-within .ripple-bar { width: 100%; }

        .eye { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--t2); cursor: pointer; padding: 4px; display: flex; align-items: center; transition: color .2s ease; outline: none; }
        .eye:hover { color: var(--t1); }

        .forgot { text-align: right; margin-top: -8px; margin-bottom: 28px; }
        .forgot a { font-size: 12px; color: var(--t2); text-decoration: none; transition: color .2s ease; }
        .forgot a:hover { color: var(--vg); }

        .btn-login { width: 100%; padding: 14px; border: none; border-radius: 12px; font-family: inherit; font-size: 15px; font-weight: 600; cursor: pointer; position: relative; overflow: hidden; background: linear-gradient(135deg, var(--vd) 0%, var(--violet) 60%, rgba(169, 112, 255, .9) 100%); color: #fff; letter-spacing: .5px; transition: all .3s cubic-bezier(.22, 1, .36, 1); box-shadow: 0 4px 24px rgba(146, 77, 191, .3); }
        .btn-login::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, transparent 40%, rgba(255, 255, 255, .1) 100%); opacity: 0; transition: opacity .3s ease; pointer-events: none; }
        .btn-login:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(146, 77, 191, .45); }
        .btn-login:hover::before { opacity: 1; }
        .btn-login:active:not(:disabled) { transform: scale(.98) translateY(0); }
        
        .btn-login.loading .btn-text { opacity: 0; }
        .btn-login.loading { cursor: default; }
        .btn-login.loading::after { content: ''; position: absolute; width: 18px; height: 18px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; top: 50%; left: 50%; transform: translate(-50%, -50%); animation: spin .8s linear infinite; }
        
        @keyframes spin { to { transform: translate(-50%, -50%) rotate(360deg); } }

        .divider { display: flex; align-items: center; gap: 12px; color: var(--t2); }
        .divider::before, .divider::after { content: ''; flex: 1; height: 1px; background: rgba(255, 255, 255, .06); }

        .error-bar { background: rgba(255, 77, 141, .08); border: 1px solid rgba(255, 77, 141, .2); border-radius: 8px; padding: 10px 14px; font-size: 12px; color: #FF8BAA; margin-bottom: 16px; align-items: center; gap: 8px; }
        .error-bar i { font-style: normal; }
      `}</style>
    </div>
  );
}